/*
 * Fullbore score formatting.
 *
 * A score is a whole number — the sum of ring values — so rendering it as
 * "48.00" invents two decimal places that do not exist and, worse, collides with
 * the notation shooters actually use: `48.5` means *48 points with 5 V-bulls*,
 * not forty-eight-point-five. Scores are therefore printed as integers, and the
 * combined form keeps the centre count where it belongs.
 *
 * Averages are genuine means and do keep their decimals — use formatAverage().
 */

/** A score exactly as it should read: 48, not 48.00. Non-integers keep 2dp. */
export const formatScore = (n: number | null | undefined): string => {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

/**
 * Conventional fullbore notation — "48.5V" is 48 points including 5 V-bulls,
 * "59.3X" is 59 points including 3 X centres (F-class).
 */
export const formatScoreCentres = (
  score: number | null | undefined,
  centres: number | null | undefined,
  centreLabel = "V"
): string => {
  if (score === null || score === undefined) return "—";
  return `${formatScore(score)}.${centres ?? 0}${centreLabel}`;
};

/** Averages are real means, so they keep two decimal places. */
export const formatAverage = (n: number | null | undefined): string =>
  n === null || n === undefined || Number.isNaN(n) ? "—" : n.toFixed(2);

/*
 * What to print in a shot table's first column.
 *
 * Sighters and record shots are numbered separately — S1 S2 then 1 to 10 —
 * because a competitor who fires two sighters and ten to count has a first
 * counting shot called 1, not 3. A struck-out shot takes no number: it was
 * not fired for record, and numbering it shifts everything after it.
 */
export const shotLabel = (shot: { label?: string | null; n: number; sighter?: boolean; deleted?: boolean }): string => {
  if (shot.label !== undefined) return shot.label ?? "—";
  // An import from before labels existed: fall back to the raw position.
  return String(shot.n);
};

/*
 * A result as a percentage of what was possible — the one figure that means
 * the same thing across a 10-shot Target Rifle match and a 60-shot ISSF card.
 * Two decimals, because a season is often decided inside one.
 */
export const formatPercent = (n: number | null | undefined): string =>
  n === null || n === undefined || Number.isNaN(n) ? "—" : `${n.toFixed(2)}%`;
